import Badge from 'components/Badge';
import withCreatePortal from 'components/HOC/withCreatePortal';
import { Icon, Icons } from 'components/Icon';
import { Loader } from 'components/Loader';
import ConfirmModal from 'components/Modal/ConfirmModal';
import TableActionWidget from 'components/TableWidget/TableActionWidget';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Child, Children } from 'service/dto/children.dto';
import { StringFormater } from 'utils';
import EditChildrenModal from './EditChildrenModal';
import { toast } from 'react-toastify';
import { Alert } from 'components/Toast';
import { useDeleteChildren } from 'common/queries-and-mutations/children';

type Props = {
  childrens: Children | undefined;
  onSuccess: () => void;
};
const EnhancedConfirmModal = withCreatePortal(ConfirmModal);
const EnhancedEditChildrenForm = withCreatePortal(EditChildrenModal);

export default function ChildrenTable({ childrens, onSuccess }: Props) {
  const [selectedId, setSelectedId] = useState<null | number>(null);
  const [selectedUser, setSelectedUser] = useState<null | Child>(null);
  const [showAskToConfirm, setShowAskToConfirm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const navigate = useNavigate();

  const {
    mutate: deleteChildren,
    isLoading: isDeleting,
    isSuccess: isDeleted
  } = useDeleteChildren();

  useEffect(() => {
    if (isDeleted) {
      toast(<Alert message="Child Successfully Deleted" type="success" />);
      onSuccess();
      setSelectedUser(null);
      setShowAskToConfirm(false);
    }
  }, [isDeleting, isDeleted]);

  const onYesClick = () => {
    if (selectedUser?.id) {
      deleteChildren(selectedUser?.id);
    } else {
      toast(
        <Alert
          message="Something went wrong, Please refresh this page and try again"
          type="error"
        />
      );
    }
  };

  return (
    <div>
      <table className="table-to-print w-full px-6 py-4 text-left text-sm">
        <thead className="border-b bg-white text-xs text-[#1F4173]">
          <tr>
            <th scope="col" className="px-6 py-4">
              Name
            </th>
            <th scope="col" className="px-6 py-4">
              Phone number
            </th>
            <th scope="col" className="px-6 py-4">
              Gender
            </th>
            <th scope="col" className="px-6 py-4">
              Status
            </th>
            <th scope="col" className="column-to-hide px-6 py-4">
              Action
            </th>
          </tr>
        </thead>
        <tbody className="px-6 py-4">
          {!childrens?.length ? (
            <tr className="text-center">
              <td colSpan={5} className="py-10 text-lg">
                No results found
              </td>
            </tr>
          ) : (
            childrens?.map(child => (
              <tr key={child.id} className="border-b bg-white hover:bg-gray-50">
                <td className="px-6 py-4 text-xs font-normal  text-[#353535]">
                  {StringFormater(
                    `${child.childFirstName} ${child.childMiddleName} ${child.childLastName}`
                  )}
                </td>
                <td className="px-6 py-4 text-xs font-normal text-[#353535]">
                  {child.phoneNumber || 'N/A'}
                </td>
                <td className="px-6 py-4 text-xs font-normal text-[#353535]">
                  {child.gender || 'N/A'}
                </td>
                <td className="px-6 py-4">
                  <Badge
                    title={child.isAssigned ? 'Assigned' : 'Unassigned'}
                    type={child.isAssigned ? 'success' : 'danger'}
                    size="sm"
                  />
                </td>
                <td className="column-to-hide px-6 py-4">
                  <button
                    type="button"
                    role="button"
                    tabIndex={-1}
                    onKeyDown={() => {
                      setSelectedId(child.id);
                    }}
                    onClick={() => {
                      setSelectedId(child.id);
                    }}
                  >
                    <Icon name={Icons.MoreMenu} />
                  </button>
                  {selectedId === child.id && isDeleting && (
                    <Loader height={20} width={20} type={'Wait'} />
                  )}
                  {selectedId === child.id && (
                    <TableActionWidget
                      onViewClick={() =>
                        navigate(`children-profile/${child.id}`)
                      }
                      onEditClick={() => {
                        setShowEditModal(true);
                        setSelectedUser(child);
                      }}
                      onRemoveClick={() => {
                        setShowAskToConfirm(true);
                        setSelectedUser(child);
                      }}
                      onDismiss={() => setSelectedId(null)}
                    />
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {showAskToConfirm && selectedUser && (
        <EnhancedConfirmModal
          content="Are you sure you want to remove this child?"
          onConfirm={onYesClick}
          onCancel={() => {
            setShowAskToConfirm(false);
            setSelectedUser(null);
          }}
          actionText="Yes, Remove"
          cancelText="No"
          title="Action Required"
          isSubmitting={isDeleting}
        />
      )}
      {showEditModal && selectedUser && (
        <EnhancedEditChildrenForm
          onClose={() => {
            setShowEditModal(false);
            setSelectedUser(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedUser(null);
            onSuccess();
          }}
          child={selectedUser}
        />
      )}
    </div>
  );
}
